import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Banknote,
  Cake,
  CalendarDays,
  Download,
  Laptop,
  LogIn,
  Megaphone,
  MessageSquare,
  PartyPopper,
  ShieldAlert,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import { PERMISSIONS, countOf, ordinal, readableName, type DashboardDto } from '@hrms/shared';
import { get } from '@/lib/api-client';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/use-auth';
import { ClockCard } from '@/features/attendance/clock-card';
import { MyRestDayWork } from '@/features/attendance/my-rest-day-work';
import { useChatStatus } from '@/features/chat/chat-api';
import { ChatAlertsCard } from '@/features/chat/alerts-card';
import { useAppUpdate } from '@/features/app/use-app-update';
import { AttendanceTodayCard } from './attendance-today-card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/common/error-state';
import { PageHeader } from '@/components/common/page-header';

interface TileProps {
  label: string;
  value: string | number;
  hint?: string;
  icon: LucideIcon;
  to?: string;
  tone?: 'default' | 'warning' | 'destructive';
}

/**
 * One number, and what it means.
 *
 * A tile with a tone is a tile asking for something, and it gets a tinted edge
 * so the eye finds it among a dozen identical boxes. The tint alone: letting
 * those span the full width instead stranded whichever tile followed them on a
 * half-empty row, and a coloured border already reads from across the desk.
 */
function Tile({ label, value, hint, icon: Icon, to, tone = 'default' }: TileProps) {
  const body = (
    <Card
      className={cn(
        // Tiles in a row are as tall as the tallest, so a two-line label does
        // not leave its neighbour floating.
        'h-full',
        to && 'transition-colors hover:border-primary/40',
        tone === 'warning' && 'border-warning/40 bg-warning/5',
        tone === 'destructive' && 'border-destructive/40 bg-destructive/5',
      )}
    >
      {/* Tighter than the default card padding: at two tiles to a phone screen,
          p-6 spends more room on air than on the number. */}
      <CardHeader className="flex flex-row items-center justify-between gap-2 p-4 pb-1.5">
        <CardTitle className="text-xs font-medium leading-snug text-muted-foreground sm:text-sm">
          {label}
        </CardTitle>
        <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p
          className={cn(
            'text-xl font-semibold tabular-nums sm:text-2xl',
            tone === 'destructive' && 'text-destructive',
            tone === 'warning' && 'text-warning',
          )}
        >
          {value}
        </p>
        {hint && (
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground sm:text-xs">{hint}</p>
        )}
      </CardContent>
    </Card>
  );

  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

/**
 * Two to a row on a phone.
 *
 * One tile per row turned twelve of them into eight screens of scrolling, for
 * numbers whose whole purpose is to be taken in at a glance.
 */
const TILE_GRID = 'grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4';

/**
 * Who is celebrating today, with the one thing worth doing about it.
 *
 * Names are the sorted form the rest of the app uses, with the department
 * so a technician in the field can place a name from the office. The link
 * goes to the chat rather than to a profile: the point of knowing is to say
 * something, and that is where it gets said.
 */
function BirthdaysCard({ people }: { people: DashboardDto['birthdays'] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Cake aria-hidden="true" className="size-4" />
          {people.length === 1 ? 'Birthday today' : 'Birthdays today'}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y">
          {people.map((person) => (
            <li key={person.employeeId} className="flex items-center gap-3 py-2">
              <span aria-hidden="true" className="text-lg">
                🎂
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{readableName(person.fullName)}</span>
                {person.departmentName && (
                  <span className="block text-xs text-muted-foreground">{person.departmentName}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
        <Link
          to="/chat"
          className="mt-2 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Say happy birthday in the chat
        </Link>
      </CardContent>
    </Card>
  );
}

/**
 * Who has been here another year, and the one thing worth doing about it.
 *
 * The years are the point, so they sit on the row rather than in a tooltip:
 * "3 years" is what somebody needs to know before they decide what to say.
 */
function AnniversariesCard({ people }: { people: DashboardDto['anniversaries'] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <PartyPopper aria-hidden="true" className="size-4" />
          {people.length === 1 ? 'Work anniversary today' : 'Work anniversaries today'}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y">
          {people.map((person) => (
            <li key={person.employeeId} className="flex items-center gap-3 py-2">
              <span aria-hidden="true" className="text-lg">
                🎉
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {readableName(person.fullName)}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {countOf(person.years, 'year')}
                  {person.departmentName ? ` · ${person.departmentName}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
        <Link
          to="/chat"
          className="mt-2 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Say congratulations in the chat
        </Link>
      </CardContent>
    </Card>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * The page's own greeting when today is the caller's day, or null when it is
 * an ordinary one and the time of day is the best the page can do.
 *
 * Both occasions can land on the same morning — somebody hired on their
 * birthday — and two stacked greetings would be worse than one sentence that
 * mentions both, so this is one function rather than two conditions.
 */
function ownGreeting(data: DashboardDto | undefined): string | null {
  const me = data?.me;
  if (!me) return null;
  const name = readableName(me.firstName);
  const years = me.anniversaryYears;
  if (me.birthdayToday && years !== null) {
    return `Happy birthday and ${ordinal(years)} work anniversary, ${name}! 🎂`;
  }
  if (me.birthdayToday) return `Happy birthday, ${name}! 🎂`;
  if (years !== null) return `Happy ${ordinal(years)} work anniversary, ${name}! 🎉`;
  return null;
}

/** The line under it, which carries the date so the greeting need not. */
function ownGreetingNote(data: DashboardDto | undefined): string | null {
  const me = data?.me;
  if (!me || !data) return null;
  const years = me.anniversaryYears;
  if (me.birthdayToday && years !== null) {
    return `${formatDate(data.today)} — ${countOf(years, 'year')} with KioBytes, and a birthday. Thank you for both.`;
  }
  if (me.birthdayToday) {
    return `${formatDate(data.today)} — everyone at KioBytes wishes you a great one.`;
  }
  if (years !== null) {
    return `${formatDate(data.today)} — ${countOf(years, 'year')} with KioBytes. Thank you for all of it.`;
  }
  return null;
}

export function DashboardPage() {
  const { user, can, canAny } = useAuth();

  const canPunch = can(PERMISSIONS.ATTENDANCE_PUNCH);
  // The same grant that gates starting a conversation: today ADMIN alone.
  const canManageChat = can(PERMISSIONS.CHAT_MANAGE);

  // Its own poll rather than a field on the dashboard payload: chat already
  // refreshes this every 30 seconds for the sidebar badge, so the tile rides
  // the same query and the two can never disagree about the count.
  const chat = useChatStatus();

  // Native only. In a browser both fields stay null and nothing renders.
  const update = useAppUpdate();

  // The endpoint scopes to what the caller may see, so an employee would get a
  // one-row card of themselves — true, but not worth the space.
  const canSeeAttendance = canAny(
    PERMISSIONS.ATTENDANCE_READ_TEAM,
    PERMISSIONS.ATTENDANCE_READ_ALL,
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => get<DashboardDto>('/dashboard'),
  });

  if (isError) {
    return <ErrorState error={error} onRetry={() => void refetch()} />;
  }

  return (
    <>
      <PageHeader
        // Their own day outranks the time of day: "Happy birthday" is the
        // greeting, not a line under "Good morning".
        title={ownGreeting(data) ?? `${greeting()}${data?.me ? `, ${readableName(data.me.firstName)}` : ''}`}
        description={
          ownGreetingNote(data) ?? (data ? formatDate(data.today) : (user?.email ?? ''))
        }
      />

      {isLoading ? (
        <div className={TILE_GRID}>
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full sm:h-28" />
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {/*
            Above even the clock card, and only ever on a company handset: an
            out of date shell is the reason a technician's back button behaves
            oddly or their camera stays on, and those are exactly the
            complaints that arrive as "the app is broken" rather than "I should
            update". Renders nothing in a browser and nothing when up to date.
          */}
          {update.available && (
            <Alert variant="warning">
              <Download aria-hidden="true" />
              <AlertTitle>A newer version of the app is available</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  You have {update.installed?.versionName ?? 'an older build'}; the current one is{' '}
                  {update.available.versionName}.{' '}
                  {update.available.notes}
                </p>
                <p>
                  {/*
                    A real anchor, not a scripted navigation. The link points at
                    another host on purpose — Capacitor keeps same-host
                    navigation inside the WebView, which cannot download, so the
                    different host is what hands it to the browser.
                  */}
                  <a
                    href={update.available.url}
                    className="font-medium underline underline-offset-2"
                  >
                    Download the update
                  </a>{' '}
                  — it opens in your browser. Tap the finished download to
                  install it over the top; nothing is lost and you stay signed
                  in.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/*
            Colleagues celebrating today, for everybody. Above the working
            parts on purpose: it is the one thing on this page somebody might
            act on for another person, and by the afternoon it is too late.
            The caller's own birthday is the greeting at the top, not a row
            here, so this renders only when somebody *else* is celebrating.
          */}
          {data && data.birthdays.length > 0 && <BirthdaysCard people={data.birthdays} />}

          {/*
            Colleagues marking a year here, on the same footing as birthdays and
            for the same reason: by the afternoon it is too late to say anything.
          */}
          {data && data.anniversaries.length > 0 && (
            <AnniversariesCard people={data.anniversaries} />
          )}

          {/*
            Whether this handset can actually raise a chat alert: the app's
            version, the notification permission, whether this launcher draws
            a number, and a button that fires the real thing.

            Administrators only, and only in the shell. Everybody's phone was
            the wrong place for it — thirty-eight people do not need a
            diagnostic on their dashboard for something that works. But when
            somebody reports silence, the four causes are all invisible from
            the outside, and this is the only way to see which one it is:
            whoever is helping them signs in on that handset and reads it.
          */}
          {chat.enabled && canManageChat && <ChatAlertsCard />}

          {/*
            First of the working parts.

            For most staff this is the only thing they open the system to do,
            twice a day, often on a phone at the start of a shift. Everything
            below is information; this is the action. Making somebody navigate
            to Attendance to find it is two taps of friction on the one task
            that has to happen before work starts.
          */}
          {canPunch && <ClockCard />}

          {/*
            Directly under the clock, because being told you are working a day
            off is the one piece of news here that changes somebody's weekend.
            It renders nothing unless this person has rest day work, so it costs
            everyone else no space.
          */}
          <MyRestDayWork />

          {/*
            The day's register, above the counts — it is the one thing here that
            changes through the day. Only for people who oversee others: a
            register of just yourself is not a register, and your own day is
            already on the clock card above.
          */}
          {canSeeAttendance && <AttendanceTodayCard />}

          {/*
            The payroll gate is the one thing on this page that blocks work
            rather than merely describing it, so it goes above the tiles.
          */}
          {data?.payroll && !data.payroll.ready && (
            <Alert variant="warning">
              <AlertTriangle aria-hidden="true" />
              <AlertTitle>Payroll cannot be calculated yet</AlertTitle>
              <AlertDescription>
                {data.payroll.blockers.join(', ')}.{' '}
                <Link
                  to="/payroll/contribution-tables"
                  className="font-medium underline underline-offset-2"
                >
                  Load the missing tables
                </Link>
                .
              </AlertDescription>
            </Alert>
          )}

          {data?.me && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground">You</h2>
              <div className={TILE_GRID}>
                <Tile
                  label="Today"
                  value={
                    data.me.clockedInAt
                      ? new Date(data.me.clockedInAt).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Not in'
                  }
                  hint={
                    data.me.clockedOutAt
                      ? `Out at ${new Date(data.me.clockedOutAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
                      : data.me.clockedInAt
                        ? 'Still clocked in'
                        : 'No punch recorded today'
                  }
                  icon={LogIn}
                  to="/attendance"
                />
                <Tile
                  label="Assets held"
                  value={data.me.assetsHeld}
                  hint="Equipment issued to you"
                  icon={Laptop}
                  to="/assets/mine"
                />
                {/*
                  Chat sits beside announcements because they answer the same
                  question — is anybody waiting on me — and a technician opening
                  this on a phone should not have to go looking in the nav to
                  find out. Rendered only when the company has chat on, which is
                  the same signal the sidebar item uses, so the two cannot
                  disagree about whether the feature exists.
                */}
                {chat.enabled && (
                  <Tile
                    label="Messages"
                    value={chat.unread}
                    hint={
                      chat.unread > 0
                        ? `${chat.unread} conversation${chat.unread === 1 ? '' : 's'} waiting`
                        : 'Nothing unread'
                    }
                    icon={MessageSquare}
                    to="/chat"
                    tone={chat.unread > 0 ? 'warning' : 'default'}
                  />
                )}
                <Tile
                  label="Announcements"
                  value={data.me.unreadAnnouncements}
                  hint={data.me.unreadAnnouncements > 0 ? 'Unread' : 'All caught up'}
                  icon={Megaphone}
                  to="/announcements"
                  tone={data.me.unreadAnnouncements > 0 ? 'warning' : 'default'}
                />
                {/*
                  A notice to explain is the only part of a disciplinary
                  process the employee has to act on, and the window to answer
                  is what they lose by not noticing. So the number shown is
                  what is waiting on them, with open cases as the quieter
                  fallback once they have replied.
                */}
                <Tile
                  label={
                    data.me.disciplinaryAwaitingResponse > 0 ? 'Awaiting your reply' : 'Disciplinary'
                  }
                  value={
                    data.me.disciplinaryAwaitingResponse > 0
                      ? data.me.disciplinaryAwaitingResponse
                      : data.me.openDisciplinaryCases
                  }
                  hint={
                    data.me.disciplinaryAwaitingResponse > 0
                      ? 'Notice to explain — answer before it is decided without you'
                      : data.me.openDisciplinaryCases > 0
                        ? 'Open case(s), nothing needed from you today'
                        : 'Nothing on record'
                  }
                  icon={ShieldAlert}
                  to="/disciplinary"
                  tone={
                    data.me.disciplinaryAwaitingResponse > 0
                      ? 'warning'
                      : data.me.openDisciplinaryCases > 0
                        ? 'warning'
                        : 'default'
                  }
                />
              </div>
            </section>
          )}

          {(data?.workforce || data?.attendance) && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground">Company</h2>
              <div className={TILE_GRID}>
                {data.workforce && (
                  <>
                    <Tile
                      label="Active employees"
                      value={data.workforce.activeEmployees}
                      hint={`${data.workforce.departments} department(s)`}
                      icon={Users}
                      to="/employees"
                    />
                    <Tile
                      label="On probation"
                      value={data.workforce.onProbation}
                      hint={`${data.workforce.recentHires} hired in the last 30 days`}
                      icon={UserCheck}
                      to="/employees"
                    />
                  </>
                )}

                {/*
                  The attendance tiles that used to sit here are now the summary
                  strip on the register card above. They were counted a
                  different way — grouped over existing attendance rows, so
                  anybody with no record at all was in neither figure — and two
                  numbers for the same question that disagree is worse than one.
                */}
              </div>
            </section>
          )}

          {(data?.leave || data?.payroll || data?.assets) && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground">Needs attention</h2>
              <div className={TILE_GRID}>
                {data.leave && (
                  <Tile
                    label="Leave awaiting approval"
                    value={data.leave.pendingApproval}
                    hint={`${data.leave.approvedUpcoming} approved and upcoming`}
                    icon={CalendarDays}
                    to="/leave"
                    tone={data.leave.pendingApproval > 0 ? 'warning' : 'default'}
                  />
                )}

                {data.payroll && (
                  <Tile
                    label="Open payroll runs"
                    value={data.payroll.openRuns}
                    hint={
                      data.payroll.periodStart
                        ? `${formatDate(data.payroll.periodStart)} – ${formatDate(data.payroll.periodEnd)}`
                        : 'No period yet'
                    }
                    icon={Banknote}
                    to="/payroll"
                  />
                )}

                {data.assets && (
                  <>
                    <Tile
                      label="Assets issued"
                      value={data.assets.issued}
                      hint={`${data.assets.available} available`}
                      icon={Laptop}
                      to="/assets"
                    />
                    <Tile
                      label="Assets needing attention"
                      value={data.assets.needingAttention}
                      hint="In repair, damaged or lost"
                      icon={AlertTriangle}
                      to="/assets"
                      tone={data.assets.needingAttention > 0 ? 'destructive' : 'default'}
                    />
                  </>
                )}
              </div>
            </section>
          )}

          {data?.payroll?.ready && (
            <p className="text-xs text-muted-foreground">
              <Badge variant="success" className="mr-2">
                Payroll ready
              </Badge>
              Statutory tables are loaded, so a run can be calculated.
            </p>
          )}
        </div>
      )}
    </>
  );
}
